// Normalizes the slider's grouped templates (quote / list_views) into a
// single item shape so the showcase panel and the immersive view can render
// both types with the same components.

export type ShowcaseImage = {
  image_url?: string;
  url?: string;
  // Per-image caption authored in the KB editor, already translated by
  // dengine. Both optional: every photo predating the editor has neither.
  title?: string;
  description?: string;
};

export type ShowcaseRate = {
  title: string;
  price: number;
  // One guest-facing line from dengine (cancellation, prepayment). Optional:
  // no quote predating the field carries it.
  conditions?: string;
};

// The group quote: one entry per requested room plus the alternative totals.
// All optional on the wire, so older quotes simply yield no group block.
export type QuotePartyMember = {
  room?: number;
  adults?: number;
  children?: number;
  title: string;
  price: number;
};

export type QuotePartyOption = {
  label: string;
  total: number;
  conditions?: string;
};

export type ShowcaseGroup = {
  members: QuotePartyMember[];
  // The first option mirrors `total`; the rest are alternatives.
  options: QuotePartyOption[];
  total: number | null;
};

const hasNumber = (value: any): value is number =>
  typeof value === "number" && Number.isFinite(value);

/**
 * The room breakdown of a group quote, or null for a single-room quote.
 * `party` is what says this is a group; `total` has a fallback because a
 * quote can carry the breakdown and not the summed figure.
 */
export function getShowcaseGroup(template: any): ShowcaseGroup | null {
  if (template?.type !== "quote") return null;

  const members: QuotePartyMember[] = Array.isArray(template.party)
    ? template.party.filter(Boolean)
    : [];
  if (members.length < 2) return null;

  const options: QuotePartyOption[] = Array.isArray(template.party_options)
    ? template.party_options.filter((option: any) => option && hasNumber(option.total))
    : [];

  const allPriced = members.every((member) => hasNumber(member.price));
  let total: number | null = null;
  if (hasNumber(template.total)) total = template.total;
  else if (hasNumber(options[0]?.total)) total = options[0].total;
  else if (allPriced)
    total = members.reduce((acc, member) => acc + member.price, 0);

  return { members, options, total };
}

export type ShowcaseItem = {
  title: string;
  description: string;
  isMarkdown: boolean;
  images: ShowcaseImage[];
  ctas: any[] | null;
  bookUrl: string | null;
  rates: ShowcaseRate[] | null;
};

export const getLowestRate = (item: ShowcaseItem): number | null => {
  if (!item.rates?.length) return null;
  return Math.min(...item.rates.map((rate) => rate.price));
};

export const DEFAULT_CURRENCY = "EUR";

// A quote can name its own currency, but an unknown or malformed code would
// make Intl.NumberFormat throw and take the whole slider down with it.
const CURRENCY_CODE = /^[A-Za-z]{3}$/;

export const getTemplateCurrency = (template: any): string => {
  const code = template?.currency || template?.currency_code;
  return typeof code === "string" && CURRENCY_CODE.test(code)
    ? code.toUpperCase()
    : DEFAULT_CURRENCY;
};

export const formatPrice = (
  price: number,
  language = "en",
  currency = DEFAULT_CURRENCY
) =>
  new Intl.NumberFormat(language, {
    style: "currency",
    currency,
    minimumFractionDigits: Number.isInteger(price) ? 0 : 2,
    maximumFractionDigits: Number.isInteger(price) ? 0 : 2,
  }).format(price);

export const isShowcaseTemplate = (template: any) =>
  template?.type === "quote" || template?.type === "list_views";

// Templates that can live in the fullscreen (immersive) view.
export const isExpandableTemplate = (template: any) =>
  isShowcaseTemplate(template) ||
  template?.type === "map" ||
  template?.type === "map_point" ||
  template?.type === "poi_map" ||
  template?.type === "itinerary" ||
  template?.type === "videos";

export function getShowcaseItems(template: any): ShowcaseItem[] {
  if (!template) return [];

  if (template.type === "quote") {
    return (template.rooms ?? []).map((room: any) => ({
      title: room.title ?? "",
      description: room.description ?? "",
      isMarkdown: false,
      images: room.images?.images ?? [],
      ctas: null,
      bookUrl: template.url ?? null,
      rates: room.rates?.length ? room.rates : null,
    }));
  }

  if (template.type === "list_views") {
    return (template.templates ?? []).map((item: any) => ({
      title: item.title ?? "",
      description: item.description ?? "",
      isMarkdown: true,
      images: item.images ?? [],
      ctas: item.ctas?.length ? item.ctas : null,
      bookUrl: null,
      rates: null,
    }));
  }

  return [];
}

export const getImageSrc = (image: ShowcaseImage) =>
  image?.image_url ?? image?.url ?? "";
